
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useEffect, useRef } from 'react';

interface OTPVerificationProps {
  phoneNumber: string;
  otp: string;
  loading: boolean;
  resendCooldown?: number;
  onOTPChange: (value: string) => void;
  onVerifyOTP: () => void;
  onResendOTP?: () => void;
  onReset: () => void;
  error?: string | null;
}

export const OTPVerification = ({
  phoneNumber,
  otp,
  loading,
  resendCooldown = 0,
  onOTPChange,
  onVerifyOTP,
  onResendOTP,
  onReset,
  error,
}: OTPVerificationProps) => {
  const verifyButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Auto-focus input on mount
  useEffect(() => {
    // Small delay to ensure the input is rendered
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Move focus to verify button when OTP is complete
  useEffect(() => {
    if (otp.length === 6 && verifyButtonRef.current) {
      verifyButtonRef.current.focus();
    }
  }, [otp]);

  // Handle key press on the verify button
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && otp.length === 6) {
      onVerifyOTP();
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-center text-sm text-gray-600">
        We sent a verification code to <span className="font-semibold">{phoneNumber}</span>
      </p>
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="ml-2">{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="flex justify-center my-8">
        <InputOTP
          ref={inputRef}
          autoFocus
          data-input-otp="true"
          maxLength={6}
          value={otp}
          onChange={onOTPChange}
          render={({ slots }) => (
            <InputOTPGroup>
              {slots.map((slot, index) => (
                <InputOTPSlot 
                  key={index} 
                  {...slot}
                  index={index}
                />
              ))}
            </InputOTPGroup>
          )}
        />
      </div>
      
      <div className="text-xs text-center text-gray-500">
        Enter the 6-digit code sent to your phone
      </div>
      
      <Button 
        ref={verifyButtonRef}
        onClick={onVerifyOTP} 
        onKeyDown={handleKeyDown}
        className="w-full"
        disabled={loading || otp.length !== 6}
      >
        {loading ? 'Verifying...' : 'Verify Code'}
      </Button>
      
      <div className="flex space-x-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={onReset}
          disabled={loading}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Change Number
        </Button>
        
        {onResendOTP && (
          <Button
            variant="outline"
            className="flex-1"
            onClick={onResendOTP}
            disabled={loading || resendCooldown > 0}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${resendCooldown > 0 ? "animate-spin" : ""}`} />
            {resendCooldown > 0 
              ? `Resend (${resendCooldown}s)` 
              : 'Resend Code'}
          </Button>
        )}
      </div>
    </div>
  );
};
